"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { ViewMode } from "@/lib/constants";

interface TOCItem {
  id: string;
  text: string;
  level: number;
}

const QUICK_TOC_ITEMS: TOCItem[] = [
  { id: "quick-guide", text: "당직 Quick Guide", level: 2 },
  { id: "three-line", text: "3줄 요약", level: 2 },
  { id: "red-flags", text: "Red Flags", level: 2 },
  { id: "treatment-update", text: "최신 치료 트렌드", level: 2 },
  { id: "key-papers", text: "핵심 논문", level: 2 },
  { id: "insurance", text: "보험 기준", level: 2 },
  { id: "practical-protocol", text: "실전 프로토콜", level: 2 },
  { id: "complication-mgmt", text: "합병증 대처", level: 2 },
  { id: "quick-differential", text: "감별 Quick Table", level: 2 },
];

interface DiseaseTOCProps {
  headings?: TOCItem[];
  viewMode?: ViewMode;
}

export function DiseaseTOC({ headings, viewMode = "standard" }: DiseaseTOCProps) {
  const [activeId, setActiveId] = useState<string>("");
  const [items, setItems] = useState<TOCItem[]>(headings || []);

  // Standard 모드: DOM에서 h2/h3 스캔
  useEffect(() => {
    if (viewMode === "quick") {
      setItems(QUICK_TOC_ITEMS);
      return;
    }

    if (headings) {
      setItems(headings);
      return;
    }

    const elements = document.querySelectorAll(
      ".prose h2[id], .prose h3[id]"
    );
    const tocItems: TOCItem[] = Array.from(elements).map((el) => ({
      id: el.id,
      text: el.textContent || "",
      level: el.tagName === "H2" ? 2 : 3,
    }));
    setItems(tocItems);
  }, [headings, viewMode]);

  // IntersectionObserver for active highlight
  useEffect(() => {
    if (viewMode === "quick") {
      // Quick 모드: data-toc-id 속성을 가진 요소 관찰
      const elements = document.querySelectorAll("[data-toc-id]");
      if (elements.length === 0) return;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const id = (entry.target as HTMLElement).dataset.tocId;
              if (id) setActiveId(id);
            }
          }
        },
        { rootMargin: "-80px 0px -70% 0px" }
      );

      elements.forEach((el) => observer.observe(el));
      return () => observer.disconnect();
    }

    // Standard 모드: prose 내 h2/h3 관찰
    const elements = document.querySelectorAll(
      ".prose h2[id], .prose h3[id]"
    );
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "-80px 0px -70% 0px" }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items, viewMode]);

  if (items.length === 0) return null;

  return (
    <nav className="space-y-1">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {viewMode === "quick" ? "Quick Clinical" : "목차"}
      </p>
      {items.map((item) => (
        <a
          key={item.id}
          href={`#${item.id}`}
          className={cn(
            "block text-sm transition-colors hover:text-foreground",
            item.level === 3 ? "pl-3" : "",
            activeId === item.id
              ? "font-medium text-primary"
              : "text-muted-foreground"
          )}
          onClick={(e) => {
            e.preventDefault();
            document.getElementById(item.id)?.scrollIntoView({
              behavior: "smooth",
            });
          }}
        >
          {item.text}
        </a>
      ))}
    </nav>
  );
}
